import type { ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState
} from 'react'

export interface WordRequest {
    word: string
    hint?: string
    context?: string
}

interface UiState {
    word: WordRequest | null
    openWord: (req: WordRequest) => void
    closeWord: () => void
    searchOpen: boolean
    setSearchOpen: (open: boolean) => void
    settingsOpen: boolean
    setSettingsOpen: (open: boolean) => void
}

const UiStateContext = createContext<UiState | null>(null)

export const UiStateProvider = ({ children }: { children: ReactNode }) => {
    const [word, setWord] = useState<WordRequest | null>(null)
    const [searchOpen, setSearchOpen] = useState(false)
    const [settingsOpen, setSettingsOpen] = useState(false)

    const openWord = useCallback((req: WordRequest) => setWord(req), [])
    const closeWord = useCallback(() => setWord(null), [])

    const value = useMemo<UiState>(() => ({
        word,
        openWord,
        closeWord,
        searchOpen,
        setSearchOpen,
        settingsOpen,
        setSettingsOpen
    }), [word, openWord, closeWord, searchOpen, settingsOpen])

    return (
        <UiStateContext.Provider value={value}>
            {children}
        </UiStateContext.Provider>
    )
}

export const useUi = (): UiState => {
    const ctx = useContext(UiStateContext)
    if (!ctx) throw new Error('useUi must be used inside UiStateProvider')
    return ctx
}
